// ML background-segmentation compositor (ADR 0070, ML variant). A drop-in
// alternative to the WebGL chroma keyer (chromaKey.ts) that needs NO green
// screen: instead of keying out a known color, it runs Google MediaPipe selfie
// segmentation per frame to find the person, then composites the person over
// the selected background image into a <canvas> and exposes
// canvas.captureStream() as the result MediaStream. The Legend's Go-Live code
// publishes / replaces its camera video track with this stream, so the
// segmented result is baked into the Legend feed BEFORE it reaches Program /
// Fans — no server-side compositing, no viewer-side work.
//
// MediaPipe (WASM + the model) is LAZY-LOADED via a dynamic import() inside
// start(), so it is code-split out of the main bundle and never evaluated in
// jsdom/tests. Segmentation is best-effort: if init fails start() rejects and
// the caller falls back to the raw camera (same contract as ChromaCompositor).

import { fitScale, type ChromaParams, type Compositor, type FitMode } from './chromaKey'
import { startFrameClock, type FrameClock } from './workerClock'

// Types only — erased at compile time, so nothing from the package is imported
// or evaluated at module load (the runtime module is dynamically imported in
// start()). This keeps the heavy WASM/model out of the main bundle and tests.
import type { ImageSegmenter, MPMask } from '@mediapipe/tasks-vision'

// MediaPipe WASM fileset (loader + .wasm), pinned to the installed version so
// the runtime matches the JS API we compiled against. jsdelivr is on the
// artifact/CSP-safe CDN list and mirrors the npm package's /wasm folder.
const WASM_FILESET_URL = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@1.0.1/wasm'

// Google-hosted general "selfie segmenter" model (float16). Outputs two
// categories — background at index 0, person at index 1 — so with confidence
// masks the person mask is confidenceMasks[1].
const SELFIE_MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/latest/selfie_segmenter.tflite'

const DEFAULT_FIT: FitMode = 'cover'
const DEFAULT_SMOOTHNESS = 0.08 // 0..1 → edge feather (see featherPx)
const IDLE_FILL = '#14181c' // neutral dark shown when there is no background image

/** Map the 0..1 `smoothness` param to a mask-edge blur radius in canvas pixels. */
function featherPx(smoothness: number): number {
  const s = Number.isFinite(smoothness) ? smoothness : DEFAULT_SMOOTHNESS
  return Math.max(0, Math.min(s * 32, 16))
}

/**
 * MLCompositor segments the person in `sourceTrack` from its background with
 * MediaPipe selfie segmentation and composites them over `background`, yielding
 * a composited MediaStream via start(). Update the background/params live with
 * setBackground / setParams. Call stop() to release the model, RAF loop, hidden
 * video, and captured stream. Implements the shared Compositor interface so the
 * Go-Live reconcile can drive it identically to the chroma keyer.
 */
export class MLCompositor implements Compositor {
  private canvas: HTMLCanvasElement
  private ctx: CanvasRenderingContext2D | null = null
  private video: HTMLVideoElement
  private maskCanvas: HTMLCanvasElement
  private maskCtx: CanvasRenderingContext2D | null = null
  private segmenter: ImageSegmenter | null = null
  private clock: FrameClock | null = null
  private lastTs = 0
  private active = false
  private bgImage: HTMLImageElement | null = null
  private fit: FitMode = DEFAULT_FIT
  private smoothness = DEFAULT_SMOOTHNESS
  stream: MediaStream | null = null

  constructor() {
    this.canvas = document.createElement('canvas')
    this.canvas.width = 1280
    this.canvas.height = 720
    this.maskCanvas = document.createElement('canvas')
    this.video = document.createElement('video')
    this.video.muted = true
    this.video.playsInline = true
    this.video.autoplay = true
  }

  /**
   * True if the environment can plausibly run the ML compositor: a real DOM
   * with canvas 2D + captureStream, createImageBitmap, and a WebGL context (the
   * MediaPipe GPU path needs it). Optimistic by design — the definitive failure
   * path is start() throwing when the model/WASM can't load. False in jsdom
   * (no real canvas/WebGL backend), so tests never try to run it.
   */
  static supported(): boolean {
    try {
      if (typeof document === 'undefined') return false
      if (typeof createImageBitmap !== 'function') return false
      const c = document.createElement('canvas')
      if (typeof c.captureStream !== 'function') return false
      const gl = c.getContext('webgl') || c.getContext('experimental-webgl')
      return !!gl
    } catch {
      return false
    }
  }

  /** Store the background fit + edge feather; color/similarity/spill are chroma-only and ignored (ML has no key color). */
  setParams(p: ChromaParams) {
    this.fit = p.fit ?? this.fit
    this.smoothness = p.smoothness ?? this.smoothness
  }

  /** Swap the background image (already-loaded HTMLImageElement, or null to clear → neutral dark fill). */
  setBackground(img: HTMLImageElement | null) {
    this.bgImage = img
  }

  /**
   * Begin compositing `sourceTrack` and resolve with the composited MediaStream
   * (canvas capture at `fps`). Async because MediaPipe (WASM + model) is
   * lazy-loaded here; rejects if init fails so the caller can publish the raw
   * camera instead.
   */
  async start(sourceTrack: MediaStreamTrack, fps = 30): Promise<MediaStream> {
    this.active = true
    this.lastTs = 0

    const ctx = this.canvas.getContext('2d')
    if (!ctx) throw new Error('2D canvas context unavailable')
    this.ctx = ctx
    this.maskCtx = this.maskCanvas.getContext('2d')
    if (!this.maskCtx) throw new Error('2D canvas context unavailable')

    // Lazy dynamic import: code-split, never evaluated at module load / in tests.
    const vision = await import('@mediapipe/tasks-vision')
    const fileset = await vision.FilesetResolver.forVisionTasks(WASM_FILESET_URL)
    const segmenter = await vision.ImageSegmenter.createFromOptions(fileset, {
      baseOptions: { modelAssetPath: SELFIE_MODEL_URL },
      runningMode: 'VIDEO',
      outputCategoryMask: false,
      outputConfidenceMasks: true,
    })

    // stop() may have been called while we awaited init — bail cleanly.
    if (!this.active) {
      segmenter.close()
      return this.stream ?? new MediaStream()
    }
    this.segmenter = segmenter

    this.video.srcObject = new MediaStream([sourceTrack])
    // Autoplay can reject on some policies; the RAF loop simply waits for frames.
    await this.video.play().catch(() => {})

    this.stream = this.canvas.captureStream(fps)
    // Background-proof Worker clock, NOT rAF (ADR 0090/0102; same reasoning as
    // ChromaCompositor). rAF freezes when backgrounded and a main-thread setTimeout
    // pump is clamped to ~1 fps while hidden — both stall the published canvas the
    // moment the Legend alt-tabs. This matters even more for ML: each tick is a
    // GPU→CPU mask readback + a per-pixel JS loop + a canvas blur. A dedicated
    // Worker's timer is not throttled, so startFrameClock keeps firing at `fps`
    // while hidden and caps the work to the published rate; it falls back to the
    // ADR 0090 self-correcting setTimeout pump if no Worker, and wraps tick() in
    // try/catch so a transient segmentation throw skips one frame, not the loop.
    this.clock = startFrameClock(fps, () => {
      if (this.active) this.tick()
    })
    return this.stream
  }

  // Swap the input camera in place (live camera switch, LSC-613). tick() reads
  // this.video every frame, so the new device's track composites seamlessly — the
  // published canvas stream + the loaded model are untouched, so fans/Director see
  // no drop.
  setSource(sourceTrack: MediaStreamTrack): void {
    this.video.srcObject = new MediaStream([sourceTrack])
    void this.video.play().catch(() => {})
  }

  /** One frame-clock step: size the canvas to the camera, segment the frame, composite. */
  private tick() {
    const ctx = this.ctx
    const seg = this.segmenter
    const video = this.video
    if (!ctx || !seg) return

    const vw = video.videoWidth
    const vh = video.videoHeight
    if (vw && vh && (this.canvas.width !== vw || this.canvas.height !== vh)) {
      this.canvas.width = vw
      this.canvas.height = vh
    }
    // No decoded frames yet: show the background (or dark fill) so the captured
    // stream is never a blank/black frame.
    if (video.readyState < 2 || !vw || !vh) {
      this.drawIdle()
      return
    }

    // MediaPipe requires strictly increasing timestamps across calls.
    let ts = performance.now()
    if (ts <= this.lastTs) ts = this.lastTs + 1
    this.lastTs = ts

    try {
      // Callback form: the masks are valid only for the callback's duration, so
      // we read + composite synchronously here (no copy, no manual close()).
      seg.segmentForVideo(video, ts, (result) => {
        const masks = result.confidenceMasks
        // Selfie general model → [background, person]; person is index 1.
        const person = masks && masks.length ? (masks.length > 1 ? masks[1] : masks[0]) : undefined
        if (person) this.composite(person)
        else this.drawIdle()
      })
    } catch {
      // A transient segmentation hiccup shouldn't kill the loop or the feed —
      // keep the previous frame on the canvas and try again next tick.
    }
  }

  /**
   * Composite the person (masked camera) over the background:
   *   1. draw the person mask as an alpha layer (feathered for a soft edge),
   *   2. `source-in` the camera → camera kept only within the mask alpha,
   *   3. `destination-over` the fitted background (+ letterbox) behind it.
   */
  private composite(person: MPMask) {
    const ctx = this.ctx
    const maskCtx = this.maskCtx
    if (!ctx || !maskCtx) return

    const w = this.canvas.width
    const h = this.canvas.height
    const mw = person.width
    const mh = person.height
    if (!mw || !mh) {
      this.drawIdle()
      return
    }

    // Build a mask canvas whose ALPHA = person confidence (RGB unused).
    const data = person.getAsFloat32Array()
    if (this.maskCanvas.width !== mw || this.maskCanvas.height !== mh) {
      this.maskCanvas.width = mw
      this.maskCanvas.height = mh
    }
    const imageData = maskCtx.createImageData(mw, mh)
    const px = imageData.data
    const n = Math.min(data.length, mw * mh)
    for (let i = 0; i < n; i++) {
      const v = data[i]
      const o = i * 4
      px[o] = 255
      px[o + 1] = 255
      px[o + 2] = 255
      px[o + 3] = v <= 0 ? 0 : v >= 1 ? 255 : (v * 255) | 0
    }
    maskCtx.putImageData(imageData, 0, 0)

    // 1. Mask alpha, upscaled to the frame with a soft feathered edge.
    ctx.globalCompositeOperation = 'source-over'
    ctx.clearRect(0, 0, w, h)
    const feather = featherPx(this.smoothness)
    ctx.filter = feather > 0.1 ? `blur(${feather}px)` : 'none'
    ctx.drawImage(this.maskCanvas, 0, 0, w, h)
    ctx.filter = 'none'

    // 2. Camera kept only where the mask alpha is set (result alpha = mask alpha).
    ctx.globalCompositeOperation = 'source-in'
    ctx.drawImage(this.video, 0, 0, w, h)

    // 3. Background behind the person.
    this.drawBackground(ctx, w, h)
    ctx.globalCompositeOperation = 'source-over'
  }

  /** Draw the background (or dark fallback) filling a cleared canvas — used before the first frame / when no person is found. */
  private drawIdle() {
    const ctx = this.ctx
    if (!ctx) return
    const w = this.canvas.width
    const h = this.canvas.height
    ctx.globalCompositeOperation = 'source-over'
    ctx.clearRect(0, 0, w, h)
    this.drawBackground(ctx, w, h)
    ctx.globalCompositeOperation = 'source-over'
  }

  /**
   * Draw the background BEHIND whatever is already on the canvas using
   * destination-over, respecting `fit` via the shared fitScale helper:
   *   - cover / stretch: crop the source region so the image fills the frame,
   *   - contain: fit the whole image centered, with black letterbox bars.
   * A final full-canvas fill (black, or neutral dark when there is no image)
   * lands below everything to paint any remaining transparent (letterbox) area.
   */
  private drawBackground(ctx: CanvasRenderingContext2D, w: number, h: number) {
    ctx.globalCompositeOperation = 'destination-over'
    const img = this.bgImage
    if (img && img.complete && img.naturalWidth && img.naturalHeight) {
      const [fx, fy] = fitScale(this.fit, img.naturalWidth, img.naturalHeight, w, h)
      if (this.fit === 'contain') {
        // fitScale returns >=1 factors → image occupies the central 1/f fraction.
        const dw = w / fx
        const dh = h / fy
        ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight, (w - dw) / 2, (h - dh) / 2, dw, dh)
      } else {
        // cover / stretch → sample a centered sub-window and stretch it to fill.
        const srcW = img.naturalWidth * fx
        const srcH = img.naturalHeight * fy
        ctx.drawImage(img, (img.naturalWidth - srcW) / 2, (img.naturalHeight - srcH) / 2, srcW, srcH, 0, 0, w, h)
      }
    }
    // Letterbox / no-image fill, beneath the image (destination-over).
    ctx.fillStyle = img ? '#000000' : IDLE_FILL
    ctx.fillRect(0, 0, w, h)
  }

  /** Stop the frame clock, stop the captured tracks, and release the model + hidden video. */
  stop() {
    this.active = false
    this.clock?.stop()
    this.clock = null
    this.lastTs = 0
    this.stream?.getTracks().forEach((t) => t.stop())
    this.stream = null
    try {
      this.segmenter?.close()
    } catch {
      /* ignore */
    }
    this.segmenter = null
    try {
      this.video.pause()
      this.video.srcObject = null
    } catch {
      /* ignore */
    }
  }
}
